import React, { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../../../utils/userSettings.js";
import { Box, Chip, Typography } from "@mui/material";
import UserAvatar from "../../../../components/UserAvatar.jsx";
import { getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import { AddCircleOutlineOutlined as AddIcon } from "@mui/icons-material";
import BlogPostStatusBadge from "./BlogPostStatusBadge.jsx";

export default function BlogPostsTable({
  onRowClick = () => {},
  onClickNew = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();

  const tableKey = "blog-posts";
  const storageKey = "v2_table_admin-blog-posts";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "News-Beiträge",
        route: "data/blog-posts",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          include: ["creator", "authors", "roles"],
          fields: [
            "id",
            "overline",
            "status",
            "headline",
            "teaser",
            "published_at",
            "created_at",
            "updated_at",
            "created_by",
          ],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKeys: ["overline", "headline", "teaser"],
      header: "Titel",
      minSize: 320,
      Cell: ({ row }) => {
        const blogPost = row.original;
        if (!blogPost) {
          return null;
        }

        return (
          <>
            <Typography variant="subtitle2" color="textSecondary">
              {blogPost.overline}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {blogPost.headline}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {blogPost.teaser}
            </Typography>
          </>
        );
      },
    },
    {
      accessorKey: "createdBy",
      header: "Erstellt",
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const creator = row.original.creator;
        if (!creator) {
          return "-";
        }
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <UserAvatar user={creator} enableTooltip={true} />
          </Box>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Erstellt am",
      size: 80,
      enableColumnFilter: false,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatumZeit(objectToRender?.created_at)}</span>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 100,
      filterVariant: "select",
      filterSelectOptions: [
        {
          value: "draft",
          label: "Entwurf",
        },
        {
          value: "review",
          label: "In Prüfung",
        },
        {
          value: "released",
          label: "Freigegeben",
        },
        {
          value: "published",
          label: "Veröffentlicht",
        },
      ],
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return (
          <>
            <BlogPostStatusBadge status={objectToRender.status} />
            {objectToRender?.published_at && (
              <div>
                <small>{getDatumZeit(objectToRender?.published_at)}</small>
              </div>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "permission",
      header: "Berechtigung",
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return (
          <>
            <Box display="flex" gap={1} flexWrap="wrap">
              {objectToRender.roles?.map((role) => (
                <Chip
                  key={`role_${role.id}`}
                  variant="outlined"
                  size="small"
                  label={role.name}
                />
              ))}
            </Box>
          </>
        );
      },
    },
    {
      accessorKey: "authors",
      header: "Autoren",
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;

        const authors = objectToRender.authors;
        return (
          <>
            <Box display="flex" gap={1} flexWrap="wrap">
              {authors.map((author) => (
                <Box key={author.id} py={1}>
                  <UserAvatar user={author} enableTooltip={true} />
                </Box>
              ))}
            </Box>
          </>
        );
      },
    },
  ];

  const toolbarActions = [
    {
      type: "button",
      variant: "primary",
      label: "Neu",
      icon: <AddIcon />,
      onClick: onClickNew,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      onRowClick={onRowClick}
      toolbarActions={toolbarActions}
    />
  );
}

BlogPostsTable.propTypes = {
  onRowClick: PropTypes.func,
};
